import { useState } from 'react';

import { PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { FieldHeader } from '@/components/atoms/FieldHeader/FieldHeader';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { selectFeatureFlags } from '@/redux/featureFlag/selectors';
import { Action, ActionType } from '@/shared/types/rules';

import { ActionChip } from './ActionChip';
import { ActionEditor } from './ActionEditor';
import {
  ACTION_ICONS,
  ACTION_LABEL_KEYS,
  ACTION_MENU_TYPES,
  MENU_PANEL_CLASS,
  MENU_WRAPPER_CLASS,
} from './constants';
import { needsActionValue } from './utils';

type ActionsMenuProps = {
  types: ActionType[];
  canAssignCollection: boolean;
  onAdd: (action: Action) => void;
};

const ActionsMenu = ({ types, canAssignCollection, onAdd }: ActionsMenuProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<ActionType | null>(null);

  const close = () => {
    setIsOpen(false);
    setSelectedType(null);
  };

  const add = (action: Action) => {
    onAdd(action);
    close();
  };

  return (
    <Popover open={isOpen} onOpenChange={(open) => (open ? setIsOpen(true) : close())}>
      <PopoverTrigger asChild>
        <Button variant="ghost-neutral" size="icon-md" aria-label={t('rules.add_action')}>
          <PlusIcon />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className={MENU_WRAPPER_CLASS}>
        {selectedType ? (
          <ActionEditor
            action={{ type: selectedType }}
            onPick={add}
            onBack={() => setSelectedType(null)}
          />
        ) : (
          <Command className={MENU_PANEL_CLASS}>
            <CommandList>
              <CommandEmpty className="px-3 py-2.5 text-sm leading-5">
                {t('rules.editor.no_available_actions')}
              </CommandEmpty>
              {types.length > 0 && (
                <CommandGroup>
                  {types.map((type) => {
                    const Icon = ACTION_ICONS[type];
                    return (
                      <CommandItem
                        key={type}
                        value={type}
                        className="h-8 gap-2"
                        disabled={type === ActionType.ASSIGN_COLLECTION && !canAssignCollection}
                        onSelect={() =>
                          needsActionValue(type) ? setSelectedType(type) : add({ type })
                        }
                      >
                        <Icon className="text-foreground-muted" />
                        {t(ACTION_LABEL_KEYS[type])}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  );
};

type ActionsFieldProps = {
  value: Action[];
  onChange: (value: Action[]) => void;
  disabled?: boolean;
  error?: string;
  showErrors?: boolean;
};

export const ActionsField = ({
  value,
  onChange,
  disabled = false,
  error,
  showErrors = false,
}: ActionsFieldProps) => {
  const { t } = useTranslation();
  const features = useSelector(selectFeatureFlags);

  const hasType = (type: ActionType) => value.some((action) => action.type === type);

  const menuTypes = ACTION_MENU_TYPES.filter(
    (type) =>
      !hasType(type) &&
      (type !== ActionType.ASSIGN_COLLECTION || features?.enable_collections_for_rules)
  );

  const add = (action: Action) => onChange([...value, action]);

  const update = (index: number, action: Action) => {
    onChange(value.map((current, i) => (i === index ? action : current)));
  };

  const remove = (index: number) => {
    const next = value.filter((_, i) => i !== index);
    const hasBrain = next.some(({ type }) => type === ActionType.ASSIGN_BRAIN);
    onChange(hasBrain ? next : next.filter(({ type }) => type !== ActionType.ASSIGN_COLLECTION));
  };

  return (
    <div className="flex flex-col gap-4">
      <FieldHeader
        title={t('rules.editor.actions_title')}
        description={t('rules.editor.actions_description')}
        error={error}
        action={
          !disabled && (
            <ActionsMenu
              types={menuTypes}
              canAssignCollection={hasType(ActionType.ASSIGN_BRAIN)}
              onAdd={add}
            />
          )
        }
      />
      {value.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          {value.map((action, index) => (
            <ActionChip
              // eslint-disable-next-line react/no-array-index-key
              key={`${action.type}-${index}`}
              action={action}
              disabled={disabled}
              showError={showErrors}
              onChange={(next) => update(index, next)}
              onRemove={() => remove(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
